import { useEffect, useState } from 'react';

import KogModalSideComponent from '@/main/components/modal/ModalSide';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import LoadingComponent from '@/main/components/shared/loading/loadingComponent';

import { getTranslateByKey } from '@/main/services/utils';
import { firstLetterToUpperCase } from '@/main/services/utilsString';

import * as S from './styles';

export interface HistoricalChange {
  field: string;
  from?: string;
  to?: string;
}

export interface HistoricalDetailsData {
  changes?: HistoricalChange[];
  groupedChanges?: HistoricalChange[][];
}

export interface IHistoricalDetails<TResponse> {
  open: boolean;
  title: string;
  actionName?: string;

  closeModal: () => void;

  loadDetails: () => Promise<TResponse>;

  mapResponse: (response: TResponse) => HistoricalDetailsData;
}

interface HistoricalDetailsContentProps {
  actionName?: string;
  data?: HistoricalDetailsData;
}

function HistoricalDetailsContent({
  actionName,
  data
}: HistoricalDetailsContentProps) {
  function renderChange(change: HistoricalChange, key: string) {
    return (
      <S.BoxLabelItem key={key}>
        <S.ContainerLabel>
          <S.LabelItem>
            {firstLetterToUpperCase(
              `${getTranslateByKey('SupplierInventoryHistory.field')}:`
            )}
          </S.LabelItem>

          <S.LabelResult>{change.field}</S.LabelResult>
        </S.ContainerLabel>

        <S.ContainerLabel>
          <S.LabelItem>{getTranslateByKey('information.from')}:</S.LabelItem>

          <S.LabelResult>{change.from ?? 'N/A'}</S.LabelResult>
        </S.ContainerLabel>

        <S.ContainerLabel>
          <S.LabelItem>{getTranslateByKey('information.to')}:</S.LabelItem>

          <S.LabelResult>{change.to ?? 'N/A'}</S.LabelResult>
        </S.ContainerLabel>
      </S.BoxLabelItem>
    );
  }

  return (
    <S.AccordionContainer>
      {!!actionName && <S.AccordionTitle>{actionName}</S.AccordionTitle>}

      {!!data?.changes?.length && (
        <S.AccordionDetailsHistory>
          {data.changes.map((change, index) =>
            renderChange(change, `change-${index}`)
          )}
        </S.AccordionDetailsHistory>
      )}

      {data?.groupedChanges?.map((group, groupIndex) => (
        <S.AccordionDetailsHistory key={groupIndex} style={{ marginTop: 16 }}>
          {group.map((change, index) =>
            renderChange(change, `${groupIndex}-${index}`)
          )}
        </S.AccordionDetailsHistory>
      ))}
    </S.AccordionContainer>
  );
}

export default function HistoricalDetails<TResponse>({
  open,
  title,
  actionName,
  closeModal,
  loadDetails,
  mapResponse
}: IHistoricalDetails<TResponse>) {
  const [loading, setLoading] = useState(false);

  const [details, setDetails] = useState<HistoricalDetailsData>();

  useEffect(() => {
    if (!open) return;

    setLoading(true);

    loadDetails()
      .then(response => {
        setDetails(mapResponse(response));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open]);

  function handleClose() {
    setDetails(undefined);
    closeModal();
  }

  return (
    <KogModalSideComponent open={open} closeModal={handleClose} title={title}>
      {loading ? (
        <LoadingComponent />
      ) : (
        <HistoricalDetailsContent actionName={actionName} data={details} />
      )}

      <div className="d-flex justify-content-center" style={{ marginTop: 10 }}>
        <SecondaryButton onClick={handleClose} id="id-historicalDetails-btn-back">
          {getTranslateByKey('information.back')}
        </SecondaryButton>
      </div>
    </KogModalSideComponent>
  );
}
